import { ChannelType } from '@vision/ui/enums';
import dayjs from 'dayjs';
import { fromApiChannelTheme } from '../channel-themes/channel-themes.mapper';
import { fromApiMobileSdkBehavior, toApiMobileSdkBehavior } from '../mobile-sdks/mobile-sdks.mapper';
import { fromApiWebWidgetBehavior, toApiWebWidgetBehavior } from '../web-widgets/web-widgets.mapper';
import {
  fromApiCatiCall,
  fromApiEmailCampaign,
  fromApiMessageCampaign,
  fromApiSmsCampaign,
  toApiCatiCall,
  toApiEmailCampaign,
  toApiMessageCampaign,
  toApiSmsCampaign,
} from './channel-campaigns.mapper';
import {
  fromApiLegacyColors,
  fromApiLegacyTheme,
  hasLegacyThemeFiles,
  toApiLegacyColors,
  toApiLegacyTheme,
} from './channel-legacy-theme.mapper';
import { channelListTypePaths } from './channel-lists.mapper';
import type {
  ChannelChangesRequestDto,
  ChannelDetailDto,
  ChannelDetailResponseDto,
  ChannelOperatingHourPeriodDto,
  CreateChannelRequestDto,
  GetChannelDetailArgs,
  UpdateChannelRequestDto,
  UpsertChannelArgs,
  UpsertChannelRequestDto,
} from './channels.api.types';
import type {
  ChannelLinkExpiration,
  ChannelOperatingHoursDay,
  ChannelOperatingHoursRange,
  ChannelUpsertType,
  ChannelValues,
  IndirectChannelType,
  SavedChannel,
} from './types';

function toApiDate(date: Date | null): string | null {
  return date ? date.toISOString() : null;
}

const channelUpsertPaths = {
  [ChannelType.CATI]: channelListTypePaths[ChannelType.CATI],
  [ChannelType.EMAIL]: channelListTypePaths[ChannelType.EMAIL],
  [ChannelType.IVR]: channelListTypePaths[ChannelType.IVR],
  [ChannelType.LINK]: channelListTypePaths[ChannelType.LINK],
  [ChannelType.MOBILE]: channelListTypePaths[ChannelType.MOBILE],
  [ChannelType.PUSH_NOTIFICATION]: channelListTypePaths[ChannelType.PUSH_NOTIFICATION],
  [ChannelType.SMS]: channelListTypePaths[ChannelType.SMS],
  [ChannelType.WEB_WIDGET]: channelListTypePaths[ChannelType.WEB_WIDGET],
  [ChannelType.APP_STORE]: channelListTypePaths[ChannelType.APP_STORE],
  [ChannelType.GLASSDOOR]: channelListTypePaths[ChannelType.GLASSDOOR],
  [ChannelType.GOOGLE_MAP]: channelListTypePaths[ChannelType.GOOGLE_MAP],
  [ChannelType.GOOGLE_PLAY]: channelListTypePaths[ChannelType.GOOGLE_PLAY],
  [ChannelType.SIKAYET_VAR]: channelListTypePaths[ChannelType.SIKAYET_VAR],
  [ChannelType.TRUSTPILOT]: channelListTypePaths[ChannelType.TRUSTPILOT],
  [ChannelType.WHATSAPP]: channelListTypePaths[ChannelType.WHATSAPP],
} satisfies Record<ChannelUpsertType, string>;

export function isChannelUpsertType(channelType: ChannelType): channelType is ChannelUpsertType {
  return channelType in channelUpsertPaths;
}

const indirectChannelTypes = new Set<ChannelType>([
  ChannelType.APP_STORE,
  ChannelType.GLASSDOOR,
  ChannelType.GOOGLE_MAP,
  ChannelType.GOOGLE_PLAY,
  ChannelType.SIKAYET_VAR,
  ChannelType.TRUSTPILOT,
  ChannelType.WHATSAPP,
] satisfies IndirectChannelType[]);

function isIndirectChannelType(channelType: ChannelType): channelType is IndirectChannelType {
  return indirectChannelTypes.has(channelType);
}

// Every other type saves through its own path, so a type whose upsert endpoint is not there yet fails its save
// (plan G5.5). Kiosk has no list of its own and no upsert: it is out of scope and its channels open legacy pages.
function toChannelSavePath(channelType: ChannelType): string {
  if (isIndirectChannelType(channelType)) {
    return 'indirect';
  }

  return channelType === ChannelType.KIOSK ? 'kiosk' : channelListTypePaths[channelType];
}

// Legacy parity: these channel types choose between a legacy and a stable survey link.
const surveyLinkChannelTypes = new Set<ChannelType>([ChannelType.EMAIL, ChannelType.LINK, ChannelType.SMS]);

export function hasSurveyLinkSettings(channelType: ChannelType): boolean {
  return surveyLinkChannelTypes.has(channelType);
}

export function toApiUpsertChannelUrl({ accountId, channelType }: UpsertChannelArgs): string {
  return `/v2/accounts/${accountId}/channels/${toChannelSavePath(channelType)}/upsert`;
}

export function toApiChannelDetailUrl({ accountId, channelId, channelType }: GetChannelDetailArgs): string {
  return `/v2/accounts/${accountId}/channels/${channelUpsertPaths[channelType]}/${channelId}`;
}

// A calendar day is sent as is; an ISO timestamp would shift it to the previous day east of UTC.
function toApiLinkExpirationSetting(linkExpiration: ChannelValues['linkExpiration']) {
  return {
    link_expiration_date: linkExpiration?.unit === 'custom' ? dayjs(linkExpiration.date).format('YYYY-MM-DD') : null,
    link_expiration_period: linkExpiration && linkExpiration.unit !== 'custom' ? linkExpiration.period : null,
    link_expiration_period_unit: linkExpiration?.unit ?? null,
  };
}

// Legacy parity: a stable link is not shortened, and a legacy link leaves `shorten_url` to the API's default.
function toApiSurveyLinkSetting(surveyLinkType: ChannelValues['surveyLinkType']) {
  if (!surveyLinkType) {
    return {};
  }

  return surveyLinkType === 'stable'
    ? { link_type: surveyLinkType, shorten_url: false }
    : { link_type: surveyLinkType };
}

// One entry per domain value, so an edit request carries exactly the changed values.
const toApiChannelValue = {
  activeDateRange: ({ end, start }: ChannelValues['activeDateRange']) => ({
    activation_end_date: toApiDate(end),
    activation_start_date: toApiDate(start),
  }),
  activationExpiredMessage: (activationExpiredMessage: string | null) => ({
    activation_expired_message: activationExpiredMessage,
  }),
  catiCall: (catiCall: ChannelValues['catiCall']) => (catiCall ? toApiCatiCall(catiCall) : {}),
  channelThemeId: (channelThemeId: string | null) => ({ channel_theme_id: channelThemeId }),
  code: (code: string) => ({ code }),
  defaultAssigneeId: (defaultAssigneeId: string | null) => ({ default_assignee_id: defaultAssigneeId }),
  emailCampaign: (emailCampaign: ChannelValues['emailCampaign']) =>
    emailCampaign ? toApiEmailCampaign(emailCampaign) : {},
  flowId: (flowId: string | null) => ({ flow_id: flowId }),
  kpis: (kpis: ChannelValues['kpis']) => ({
    node_kpis: kpis.map(({ flowId, kpi, questionId }) => ({ flow_id: flowId, kpi, question_id: questionId })),
  }),
  languageId: (languageId: string | null) => ({ default_language_id: languageId }),
  legacyColors: toApiLegacyColors,
  legacyTheme: toApiLegacyTheme,
  linkExpiration: (linkExpiration: ChannelValues['linkExpiration']) => ({
    channel_setting_attributes: toApiLinkExpirationSetting(linkExpiration),
  }),
  mobileBehavior: (mobileBehavior: ChannelValues['mobileBehavior']) =>
    mobileBehavior ? toApiMobileSdkBehavior(mobileBehavior) : {},
  name: (name: string) => ({ name }),
  operatingHours: ({ alwaysOpen, outsideHoursFlowId, ranges }: ChannelValues['operatingHours']) => ({
    non_operating_hours_flow_id: outsideHoursFlowId,
    operating_24_7: alwaysOpen,
    operating_hours_attributes: ranges.map(({ day, endHour, startHour }) => ({
      end_hour: endHour,
      period: day,
      start_hour: startHour,
    })),
  }),
  parentChannelId: (parentChannelId: string | null) => ({ parent_id: parentChannelId }),
  pushCampaign: (pushCampaign: ChannelValues['pushCampaign']) =>
    pushCampaign ? toApiMessageCampaign(pushCampaign) : {},
  requireFeedbackCaptcha: (requireFeedbackCaptcha: boolean) => ({ require_feedback_captcha: requireFeedbackCaptcha }),
  showPisanoBranding: (showPisanoBranding: boolean) => ({ pisano_branding: showPisanoBranding }),
  smsCampaign: (smsCampaign: ChannelValues['smsCampaign']) => (smsCampaign ? toApiSmsCampaign(smsCampaign) : {}),
  status: (status: ChannelValues['status']) => ({ status }),
  surveyLinkType: (surveyLinkType: ChannelValues['surveyLinkType']) =>
    surveyLinkType ? { channel_setting_attributes: toApiSurveyLinkSetting(surveyLinkType) } : {},
  themeEngine: (themeEngine: ChannelValues['themeEngine']) => ({ theme_engine: themeEngine }),
  useSignatureVerification: (useSignatureVerification: boolean) => ({
    use_signature_verify: useSignatureVerification,
  }),
  whatsappCampaign: (whatsappCampaign: ChannelValues['whatsappCampaign']) =>
    whatsappCampaign ? toApiMessageCampaign(whatsappCampaign) : {},
  widgetBehavior: (widgetBehavior: ChannelValues['widgetBehavior']) =>
    widgetBehavior ? toApiWebWidgetBehavior(widgetBehavior) : {},
} satisfies { [Key in keyof ChannelValues]: (value: ChannelValues[Key]) => ChannelChangesRequestDto };

const toApiChannelChange: { [Key in keyof ChannelValues]: (value: ChannelValues[Key]) => ChannelChangesRequestDto } =
  toApiChannelValue;

function toApiCreateChannel(values: ChannelValues): CreateChannelRequestDto {
  return {
    ...toApiChannelValue.activeDateRange(values.activeDateRange),
    ...toApiChannelValue.activationExpiredMessage(values.activationExpiredMessage),
    ...toApiChannelValue.catiCall(values.catiCall),
    ...toApiChannelValue.channelThemeId(values.channelThemeId),
    ...toApiChannelValue.code(values.code),
    ...toApiChannelValue.defaultAssigneeId(values.defaultAssigneeId),
    ...toApiChannelValue.emailCampaign(values.emailCampaign),
    ...toApiChannelValue.flowId(values.flowId),
    ...toApiChannelValue.kpis(values.kpis),
    ...toApiChannelValue.languageId(values.languageId),
    ...toApiChannelValue.legacyColors(values.legacyColors),
    ...toApiChannelValue.legacyTheme(values.legacyTheme),
    channel_setting_attributes: {
      ...toApiLinkExpirationSetting(values.linkExpiration),
      ...toApiSurveyLinkSetting(values.surveyLinkType),
    },
    ...toApiChannelValue.mobileBehavior(values.mobileBehavior),
    ...toApiChannelValue.name(values.name),
    ...toApiChannelValue.operatingHours(values.operatingHours),
    ...toApiChannelValue.parentChannelId(values.parentChannelId),
    ...toApiChannelValue.pushCampaign(values.pushCampaign),
    ...toApiChannelValue.requireFeedbackCaptcha(values.requireFeedbackCaptcha),
    ...toApiChannelValue.showPisanoBranding(values.showPisanoBranding),
    ...toApiChannelValue.smsCampaign(values.smsCampaign),
    ...toApiChannelValue.status(values.status),
    ...toApiChannelValue.themeEngine(values.themeEngine),
    ...toApiChannelValue.useSignatureVerification(values.useSignatureVerification),
    ...toApiChannelValue.whatsappCampaign(values.whatsappCampaign),
    ...toApiChannelValue.widgetBehavior(values.widgetBehavior),
  };
}

// Values that share the channel setting are merged into one nested attributes object.
function mergeChannelChange(
  request: UpdateChannelRequestDto,
  change: ChannelChangesRequestDto,
): UpdateChannelRequestDto {
  const channelSetting = request.channel_setting_attributes &&
    change.channel_setting_attributes && {
      channel_setting_attributes: { ...request.channel_setting_attributes, ...change.channel_setting_attributes },
    };

  return { ...request, ...change, ...channelSetting };
}

function toApiChange<Key extends keyof ChannelValues>(
  key: Key,
  value: ChannelValues[Key] | undefined,
): ChannelChangesRequestDto {
  return value === undefined ? {} : toApiChannelChange[key](value);
}

export function toApiUpsertChannel(args: UpsertChannelArgs): UpsertChannelRequestDto {
  if ('values' in args) {
    const { channelType } = args;

    return {
      ...toApiCreateChannel(args.values),
      ...(isIndirectChannelType(channelType) ? { type: channelType } : {}),
    };
  }

  const { changes, channelId } = args;

  return (Object.keys(changes) as (keyof ChannelValues)[]).reduce<UpdateChannelRequestDto>(
    (request, key) => mergeChannelChange(request, toApiChange(key, changes[key])),
    { id: channelId },
  );
}

// The days of the form each saved schedule period covers.
const operatingHoursDaysByPeriod = {
  friday: ['weekdays'],
  monday: ['weekdays'],
  saturday: ['saturday'],
  sunday: ['sunday'],
  thursday: ['weekdays'],
  tuesday: ['weekdays'],
  wednesday: ['weekdays'],
  week: ['weekdays', 'saturday', 'sunday'],
  weekdays: ['weekdays'],
  weekend: ['saturday', 'sunday'],
} satisfies Record<ChannelOperatingHourPeriodDto, ChannelOperatingHoursDay[]>;

function fromApiOperatingHoursRanges(hours: ChannelDetailDto['operating_hours']): ChannelOperatingHoursRange[] {
  const ranges = hours.flatMap(({ end_hour, period, start_hour }) =>
    operatingHoursDaysByPeriod[period].map((day) => ({ day, endHour: end_hour, startHour: start_hour })),
  );
  const rangeKeys = ranges.map(({ day, endHour, startHour }) => `${day}-${startHour}-${endHour}`);

  return ranges.filter((_range, index) => rangeKeys.indexOf(rangeKeys[index]) === index);
}

function fromApiLinkExpiration(setting: ChannelDetailDto['channel_setting']): ChannelLinkExpiration | null {
  const unit = setting?.link_expiration_period_unit;

  if (!setting || !unit) {
    return null;
  }

  if (unit === 'custom') {
    return setting.link_expiration_date ? { date: dayjs(setting.link_expiration_date).toDate(), unit } : null;
  }

  return { period: setting.link_expiration_period ?? 1, unit };
}

function fromApiDate(date: string | null): Date | null {
  return date ? new Date(date) : null;
}

export function fromApiChannelDetail({ data }: ChannelDetailResponseDto, channelType: ChannelType): SavedChannel {
  return {
    channelTheme: data.channel_theme ? fromApiChannelTheme(data.channel_theme) : null,
    flow: data.flow && { id: data.flow.id, isConversational: data.flow.is_conversational, name: data.flow.name },
    id: data.id,
    values: {
      activeDateRange: { end: fromApiDate(data.activation_end_date), start: fromApiDate(data.activation_start_date) },
      activationExpiredMessage: data.activation_expired_message,
      catiCall: data.object === 'cati_channel' ? fromApiCatiCall(data) : null,
      channelThemeId: data.channel_theme_id,
      code: data.code ?? '',
      defaultAssigneeId: data.default_assignee_id,
      emailCampaign: data.object === 'email_channel' ? fromApiEmailCampaign(data) : null,
      flowId: data.flow_id,
      kpis: data.node_kpis.map(({ flow_id, kpi, question_id }) => ({ flowId: flow_id, kpi, questionId: question_id })),
      languageId: data.default_language_id,
      legacyColors: hasLegacyThemeFiles(channelType) ? fromApiLegacyColors(data) : null,
      legacyTheme: hasLegacyThemeFiles(channelType) && 'logo_url' in data ? fromApiLegacyTheme(data) : null,
      linkExpiration: fromApiLinkExpiration(data.channel_setting),
      mobileBehavior: data.object === 'mobile_channel' ? fromApiMobileSdkBehavior(data) : null,
      name: data.name,
      operatingHours: {
        alwaysOpen: data.operating_24_7,
        outsideHoursFlowId: data.non_operating_hours_flow_id,
        ranges: data.operating_24_7 ? [] : fromApiOperatingHoursRanges(data.operating_hours),
      },
      parentChannelId: data.parent_id,
      pushCampaign: data.object === 'push_notification_channel' ? fromApiMessageCampaign(data) : null,
      requireFeedbackCaptcha: data.require_feedback_captcha,
      showPisanoBranding: data.pisano_branding,
      smsCampaign: data.object === 'sms_channel' ? fromApiSmsCampaign(data) : null,
      status: data.status,
      // Legacy parity: a channel without a saved link type uses a legacy link.
      surveyLinkType: hasSurveyLinkSettings(channelType) ? (data.channel_setting?.link_type ?? 'legacy') : null,
      themeEngine: data.theme_engine,
      useSignatureVerification: data.use_signature_verify,
      whatsappCampaign: data.object === 'whatsapp_channel' ? fromApiMessageCampaign(data) : null,
      widgetBehavior: data.object === 'web_channel' ? fromApiWebWidgetBehavior(data) : null,
    },
    widgetCode: data.object === 'web_channel' ? data.widget_code : null,
  };
}
